import type { PageExperience } from '@/features/experiences/types';
import {
  BlockTypes,
  type BlockLayouts,
  type ExperienceLayouts,
} from '@/types/gql.generated';
import { Flex, Icon } from '@/ui';
import {
  SidebarCard,
  SidebarIconChip,
  SidebarRow,
  SidebarRowLabel,
  SidebarSection,
} from '../SidebarKit';
import { SidebarRowSwitch } from '../SidebarRowSwitch';
import { useCustomizeControl } from '../useCustomizeControl';
import { getGroupByOptions } from './groupBy';
import { GroupBySelect } from './GroupBySelect';
import { WeekStartSelect } from './WeekStartSelect';

type Props = {
  showShadeWeekends: boolean;
  showMuteWeekendDates: boolean;
  showHighlightToday: boolean;
  // Month cards have no day-of-week grid, so no column order to move.
  showWeekStart: boolean;
  showDatesInPrimaryColor: boolean;
  experience: PageExperience;
  onSetBlockLayout: (blockId: string, layout: BlockLayouts) => void;
  onTrackGroupBy: (
    collectionLayout: BlockLayouts,
    pageLayout: ExperienceLayouts
  ) => void;
};

export const DisplaySection = ({
  showShadeWeekends,
  showMuteWeekendDates,
  showHighlightToday,
  showWeekStart,
  showDatesInPrimaryColor,
  experience,
  onSetBlockLayout,
  onTrackGroupBy,
}: Props) => {
  const { registerToggle } = useCustomizeControl();
  const collectionBlockOptions = experience.layout.blocks.find(
    (item) => item.type === BlockTypes.Collection
  );
  // Group by is available wherever the layout's Collection block offers more
  // than one grouping — an inherently list-only capability, so no layout-id or
  // feature-flag gate is needed.
  const showGroupBy =
    getGroupByOptions(collectionBlockOptions?.layouts ?? []).length > 1;

  if (
    !showDatesInPrimaryColor &&
    !showShadeWeekends &&
    !showMuteWeekendDates &&
    !showHighlightToday &&
    !showWeekStart &&
    !showGroupBy
  ) {
    return null;
  }

  return (
    <SidebarSection label="Display">
      <SidebarCard overflow="visible">
        {showDatesInPrimaryColor && (
          <SidebarRow>
            <Flex align="center" gap={2.5} minW={0}>
              <SidebarIconChip size="26px">
                <Icon color="ink.soft" icon="Palette" size={3.5} />
              </SidebarIconChip>
              <SidebarRowLabel label="Dates in primary color" />
            </Flex>
            <SidebarRowSwitch
              {...registerToggle('child', 'datesInPrimaryColor')}
            />
          </SidebarRow>
        )}
        {showShadeWeekends && (
          <SidebarRow>
            <Flex align="center" gap={2.5} minW={0}>
              <SidebarIconChip size="26px">
                <Icon color="ink.soft" icon="Contrast" size={3.5} />
              </SidebarIconChip>
              <SidebarRowLabel label="Shade weekends" />
            </Flex>
            <SidebarRowSwitch
              {...registerToggle('child', 'shadeWeekendsEnabled')}
            />
          </SidebarRow>
        )}
        {showMuteWeekendDates && (
          <SidebarRow>
            <Flex align="center" gap={2.5} minW={0}>
              <SidebarIconChip size="26px">
                <Icon color="ink.soft" icon="EyeOff" size={3.5} />
              </SidebarIconChip>
              <SidebarRowLabel label="Mute weekend dates" />
            </Flex>
            <SidebarRowSwitch
              {...registerToggle('child', 'muteWeekendDatesEnabled')}
            />
          </SidebarRow>
        )}
        {showHighlightToday && (
          <SidebarRow>
            <Flex align="center" gap={2.5} minW={0}>
              <SidebarIconChip size="26px">
                <Icon color="ink.soft" icon="Sun" size={3.5} />
              </SidebarIconChip>
              <SidebarRowLabel label="Highlight today" />
            </Flex>
            <SidebarRowSwitch {...registerToggle('child', 'highlightToday')} />
          </SidebarRow>
        )}
        {showWeekStart && (
          <SidebarRow>
            <Flex align="center" flexShrink={0} gap={2.5}>
              <SidebarIconChip size="26px">
                <Icon color="ink.soft" icon="Calendar" size={3.5} />
              </SidebarIconChip>
              <SidebarRowLabel label="Week starts on" />
            </Flex>
            <WeekStartSelect />
          </SidebarRow>
        )}
        {showGroupBy && (
          <SidebarRow>
            <Flex align="center" flexShrink={0} gap={2.5}>
              <SidebarIconChip size="26px">
                <Icon color="ink.soft" icon="Layout" size={3.5} />
              </SidebarIconChip>
              <SidebarRowLabel label="Group by" />
            </Flex>
            <GroupBySelect
              experience={experience}
              onSetBlockLayout={onSetBlockLayout}
              onTrackGroupBy={onTrackGroupBy}
            />
          </SidebarRow>
        )}
      </SidebarCard>
    </SidebarSection>
  );
};
